/**
 * The platform media library (`/media`, contract §5): list, upload, alt-text edit, usage,
 * delete. `items` stays newest first, as the server orders it.
 *
 * Writes resolve with the server's answer and keep `items` in step with it; they REJECT
 * with the axios error on failure (the caller owns the message — `siteErrorMessage`, and
 * `mediaInUseReferences` for a refused delete). Every successful write calls
 * `invalidateSiteContent()`: an alt text is read by the public landing page.
 *
 * @param {{ api?: typeof siteApi, enabled?: boolean }} [options] `enabled: false` defers
 *   the first read (the picker loads only once it is opened).
 * @returns {{
 *   status: "idle" | "loading" | "ready" | "error", items: object[], error: unknown,
 *   reload: () => Promise<void>,
 *   upload: (file: File) => Promise<object>,
 *   updateAlt: (mediaId: string, patch: {alt_fr?: string, alt_en?: string, alt_es?: string}) => Promise<object>,
 *   usage: (mediaId: string) => Promise<object[]>,
 *   remove: (mediaId: string) => Promise<void>,
 * }}
 */
import { useCallback, useEffect, useState } from "react";
import siteApi from "./siteApi";
import { invalidateSiteContent } from "../../lib/siteContentInvalidation";

/**
 * Why the server would refuse `file` before it is sent, or `null`. The server decides the
 * type by the file's magic bytes, so a passing `file.type` is a UX precheck, not a
 * guarantee — the upload can still answer 415.
 *
 * @param {File} file
 * @param {{ media_types: string[], limits: { media_bytes: number } }} schema
 * @returns {null | "type" | "size"}
 */
export function uploadProblem(file, schema) {
  if (!schema.media_types.includes(file.type)) return "type";
  if (file.size > schema.limits.media_bytes) return "size";
  return null;
}

export default function usePlatformMedia({ api = siteApi, enabled = true } = {}) {
  const [status, setStatus] = useState(enabled ? "loading" : "idle");
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setStatus("loading");
    try {
      const body = await api.listMedia();
      setItems(Array.isArray(body?.items) ? body.items : []);
      setError(null);
      setStatus("ready");
    } catch (e) {
      setError(e);
      setStatus("error");
    }
  }, [api]);

  useEffect(() => { if (enabled) reload(); }, [enabled, reload]);

  const upload = useCallback(async (file) => {
    const media = await api.uploadMedia(file);
    setItems((prev) => [media, ...prev.filter((m) => m.media_id !== media.media_id)]);
    invalidateSiteContent();
    return media;
  }, [api]);

  const updateAlt = useCallback(async (mediaId, patch) => {
    const media = await api.updateMedia(mediaId, patch);
    setItems((prev) => prev.map((m) => (m.media_id === mediaId ? media : m)));
    invalidateSiteContent();
    return media;
  }, [api]);

  const usage = useCallback(async (mediaId) => {
    const body = await api.mediaUsage(mediaId);
    return Array.isArray(body?.references) ? body.references : [];
  }, [api]);

  const remove = useCallback(async (mediaId) => {
    await api.deleteMedia(mediaId);
    setItems((prev) => prev.filter((m) => m.media_id !== mediaId));
    invalidateSiteContent();
  }, [api]);

  return { status, items, error, reload, upload, updateAlt, usage, remove };
}
